import { Helmet } from "react-helmet-async";

// Canonical production origin used for canonical URLs, sitemap references and
// social share URLs. Read from REACT_APP_SITE_ORIGIN so a domain change never
// requires a code edit; falls back to the current production host.
// Intentionally NOT `window.location.origin` — canonicals must always point to
// the canonical HTTPS www domain even when the app is loaded from a preview
// URL, otherwise search engines index the wrong host.
export const SITE_ORIGIN =
  process.env.REACT_APP_SITE_ORIGIN || "https://www.whatdowewant.uk";
export const SITE_NAME = "What Do We Want? When Do We Want It?";
const DEFAULT_DESCRIPTION =
  "Conversations about Politics and Media. A moderated forum for essays, polls, interviews, and public debate about what we expect from politics and politicians.";
const DEFAULT_IMAGE = `${SITE_ORIGIN}/logo.png`;

// Trim leading whitespace / strip markdown before pushing text through OG /
// meta tags. Descriptions above ~160 chars get truncated with an ellipsis so
// Google/Bing/Twitter don't cut mid-word.
export function makeMetaDescription(raw, limit = 158) {
  if (!raw) return DEFAULT_DESCRIPTION;
  const cleaned = String(raw)
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, "$1") // strip image markdown
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1") // strip link markdown
    .replace(/[*_`~]+/g, "") // strip inline markers
    .replace(/\s+/g, " ")
    .trim();
  if (cleaned.length <= limit) return cleaned;
  return cleaned.slice(0, limit - 1).trimEnd() + "…";
}

export default function SEO({
  title,
  description,
  path = "/",
  image = DEFAULT_IMAGE,
  type = "website",
  noindex = false,
  structuredData = null,
  publishedAt = null,
  updatedAt = null,
  authorName = null,
}) {
  const url = `${SITE_ORIGIN}${path}`;
  const fullTitle = title ? `${title} — ${SITE_NAME}` : SITE_NAME;
  const desc = description || DEFAULT_DESCRIPTION;
  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      {!noindex && <meta name="robots" content="index,follow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content="en_GB" />
      {publishedAt && <meta property="article:published_time" content={publishedAt} />}
      {updatedAt && <meta property="article:modified_time" content={updatedAt} />}
      {authorName && <meta property="article:author" content={authorName} />}

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={image} />

      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
}
